import React from 'react';
import {
  MapPin,
  Clock,
  Camera,
  ImagePlus,
  X,
} from 'lucide-react';
import { InfoTip, Toggle } from './LinksConfigUi';
import LinksConfigSchedulePanel from './LinksConfigSchedulePanel';
import { hasRegisteredOpeningHours } from '../pages/admin/linksConfig.utils';

export default function LinksConfigIdentityPanel({
  hoursCopyOptions = [],
  restaurant,
  setRestaurant,
  coverEditStyle,
  identityUploadError,
  isUploadingImage,
  uploadingImage,
  removeCoverImage,
  removeProfileImage,
  handleCoverImageSelect,
  handleProfileImageSelect,
  toggleRestaurantFlag,
}) {
  const hasHours = hasRegisteredOpeningHours(restaurant.openingHours);
  return (
          <section className="fsl-card">
            <div className="fsl-card-head">
              <h2 className="fsl-card-title">
                Identidade da página
                <InfoTip text="Capa, logo, nome e informações que aparecem no topo da página." />
              </h2>
            </div>

            <div className="fsl-media">
              <div className="fsl-media-cover" style={coverEditStyle}>
                {!restaurant.coverImage && (
                  <span className="fsl-media-cover-hint">
                    <ImagePlus size={18} strokeWidth={2.2} />
                    Foto de capa
                  </span>
                )}
                <div className="fsl-media-cover-actions">
                  {restaurant.coverImage && (
                    <button
                      type="button"
                      className="fsl-media-btn fsl-media-btn--ghost"
                      onClick={removeCoverImage}
                      disabled={isUploadingImage}
                    >
                      <X size={15} strokeWidth={2.5} />
                      Remover
                    </button>
                  )}
                  <label
                    className={`fsl-media-btn ${uploadingImage === 'cover' ? 'is-loading' : ''} ${isUploadingImage ? 'is-disabled' : ''}`}
                    title={restaurant.coverImage ? 'Trocar foto de capa' : 'Adicionar foto de capa'}
                  >
                    <Camera size={15} strokeWidth={2.3} />
                    {uploadingImage === 'cover'
                      ? 'Enviando...'
                      : restaurant.coverImage
                        ? 'Trocar capa'
                        : 'Adicionar capa'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={isUploadingImage}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        handleCoverImageSelect(file);
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="fsl-media-avatar">
                <div className="fsl-media-avatar-inner">
                  {restaurant.profileImage ? (
                    <img src={restaurant.profileImage} alt="Logo" />
                  ) : (
                    <span>{restaurant.monogram}</span>
                  )}
                </div>
                {restaurant.profileImage && (
                  <button
                    type="button"
                    className="fsl-media-avatar-remove"
                    onClick={removeProfileImage}
                    disabled={isUploadingImage}
                    title="Remover logo"
                    aria-label="Remover logo"
                  >
                    <X size={13} strokeWidth={2.6} />
                  </button>
                )}
                <label
                  className={`fsl-media-avatar-cam ${uploadingImage === 'profile' ? 'is-loading' : ''} ${isUploadingImage ? 'is-disabled' : ''}`}
                  title={restaurant.profileImage ? 'Trocar logo' : 'Adicionar logo'}
                >
                  <Camera size={14} strokeWidth={2.4} />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={isUploadingImage}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      handleProfileImageSelect(file);
                    }}
                  />
                </label>
              </div>
            </div>

            {identityUploadError && (
              <p className="fsl-upload-error">{identityUploadError}</p>
            )}

            <div className="fsl-field">
              <span className="fsl-field-label">
                Nome exibido
                <InfoTip text="Nome do restaurante exibido no topo da página pública." />
              </span>
              <input
                className="fsl-input"
                value={restaurant.name}
                onChange={(event) =>
                  setRestaurant((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Nome do restaurante"
                maxLength={48}
              />
            </div>

            <div className="fsl-field">
              <span className="fsl-field-label">
                Subtítulo
                <InfoTip text="Frase curta que aparece logo abaixo do nome." />
              </span>
              <input
                className="fsl-input"
                value={restaurant.tagline}
                onChange={(event) =>
                  setRestaurant((current) => ({ ...current, tagline: event.target.value }))
                }
                placeholder="Uma frase curta sobre o restaurante"
                maxLength={90}
              />
            </div>

            <div className="fsl-field-row">
              <div className="fsl-field">
                <div className="fsl-field-head">
                  <span className="fsl-field-label">
                    Endereço
                    <InfoTip text="Endereço exibido na página. Use o botão ao lado para exibir ou ocultar." />
                  </span>
                  <Toggle
                    checked={restaurant.showAddress !== false}
                    onChange={() => toggleRestaurantFlag('showAddress')}
                    label="Exibir endereço na página"
                  />
                </div>
                <div className={`fsl-input-icon ${restaurant.showAddress === false ? 'is-dim' : ''}`}>
                  <MapPin size={15} strokeWidth={2.2} />
                  <input
                    className="fsl-input"
                    value={restaurant.address || ''}
                    onChange={(event) =>
                      setRestaurant((current) => ({ ...current, address: event.target.value }))
                    }
                    placeholder="Rua, número · cidade"
                    maxLength={70}
                  />
                </div>
              </div>

              <div className="fsl-field">
                <div className="fsl-field-head">
                  <span className="fsl-field-label">
                    Resumo dos horários (opcional)

                    <InfoTip text="Texto informativo exibido na página. Cadastre os horários em Configurar horários para habilitar o resumo." />

                  </span>
                  <Toggle
                    checked={hasHours && restaurant.showHours !== false}
                    disabled={!hasHours}

                    onChange={() => toggleRestaurantFlag('showHours')}
                    label="Exibir resumo dos horários na página"

                  />
                </div>
                <div className={`fsl-input-icon ${restaurant.showHours === false ? 'is-dim' : ''}`}>
                  <Clock size={15} strokeWidth={2.2} />
                  <input
                    className="fsl-input"
                    value={restaurant.hours || ''}                      disabled={!hasHours}

                    onChange={(event) =>
                      setRestaurant((current) => ({ ...current, hours: event.target.value }))
                    }
                    placeholder="Ex.: terça a sexta, 18h às 23h"
                    maxLength={60}
                  />
                </div>
                  {!hasHours && <p className="fsl-field-hint">Cadastre ao menos um período de funcionamento para ativar o resumo.</p>}

              </div>
            </div>

            <div className="fsl-field">
              <LinksConfigSchedulePanel restaurant={restaurant} setRestaurant={setRestaurant} copyOptions={hoursCopyOptions} />
            </div>

            <div className="fsl-inline-toggle">

              <span className="fsl-inline-toggle-title">
                Botão "Como chegar"
                <InfoTip text="Abre o endereço no Google Maps direto da página pública." />
              </span>
              <Toggle
                checked={restaurant.showDirections !== false}
                onChange={() => toggleRestaurantFlag('showDirections')}
                label="Exibir botão Como chegar"
              />
            </div>
          </section>
  );
}
